import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Send, Mic, MicOff, Loader } from 'lucide-react';
import { ThinkingIndicator } from './ThinkingIndicator';

interface ChatInputProps {
  onSend: (message: string) => void;
  isLoading: boolean;
  placeholder?: string;
  suggestedQuestions?: string[];
  showSuggestions?: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  isLoading,
  placeholder = 'Ask a question...',
  suggestedQuestions = [],
  showSuggestions = false,
}) => {
  const [input, setInput] = useState('');
  const [isListening, setIsListening] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const recognitionRef = useRef<any>(null);
  const silenceTimerRef = useRef<number | null>(null);
  const handleSendRef = useRef<(() => void) | null>(null);

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-GB';

      recognition.onresult = (event: any) => {
        let transcript = '';
        for (let i = 0; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        setInput(transcript);

        // Reset silence timer on each result
        if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
        if (event.results[event.results.length - 1].isFinal) {
          silenceTimerRef.current = window.setTimeout(() => {
            recognition.stop();
            setIsListening(false);
            handleSendRef.current?.();
          }, 2000);
        }
      };

      recognition.onerror = () => {
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current = recognition;
    }

    return () => {
      recognitionRef.current?.stop();
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    };
  }, []);

  const handleSend = useCallback(() => {
    if (!input.trim() || isLoading) return;
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
    onSend(input.trim());
    setInput('');
  }, [input, isLoading, isListening, onSend]);

  useEffect(() => {
    handleSendRef.current = handleSend;
  }, [handleSend]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const toggleListening = () => {
    if (!recognitionRef.current) return;
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
    } else {
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch {
        // Already started
      }
    }
  };

  // Expose focus and input setter for external triggers (e.g. "Ask about this" popover)
  useEffect(() => {
    (window as any).__askSidebarFocusInput = () => inputRef.current?.focus();
    (window as any).__askSidebarSetInput = (text: string) => { setInput(text); };
    return () => {
      delete (window as any).__askSidebarFocusInput;
      delete (window as any).__askSidebarSetInput;
    };
  }, []);

  return (
    <div className="space-y-2">
      {/* Suggested questions */}
      {showSuggestions && suggestedQuestions.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] font-medium text-medical-500 dark:text-medical-400 uppercase tracking-wider">Suggestions</p>
          <div className="space-y-1">
            {suggestedQuestions.map((q, i) => (
              <button
                key={i}
                onClick={() => { setInput(q); inputRef.current?.focus(); }}
                className="w-full text-left text-xs px-3 py-2 bg-white/80 dark:bg-medical-700/50 border border-medical-200 dark:border-medical-600 rounded-lg hover:border-primary-500 dark:hover:border-primary-700 hover:bg-primary-50/50 dark:hover:bg-primary-900/10 text-medical-600 dark:text-medical-300 transition-all group"
              >
                <span className="text-primary-500 dark:text-primary-500 mr-1.5 group-hover:text-primary-600 dark:group-hover:text-primary-500 transition-colors">&rarr;</span>
                {q}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input row */}
      <div className="flex items-end gap-1.5">
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={isListening ? 'Listening...' : placeholder}
          rows={2}
          disabled={isListening}
          className="flex-1 px-3 py-2 text-sm border border-medical-300 dark:border-medical-600 dark:bg-medical-700 dark:text-medical-100 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:placeholder-medical-400"
        />

        {/* Mic button */}
        {recognitionRef.current && (
          <button
            onClick={toggleListening}
            disabled={isLoading}
            className={`p-2 rounded-lg transition-all flex-shrink-0 ${
              isListening
                ? 'bg-red-600 text-white hover:bg-red-700 relative'
                : 'bg-medical-100 dark:bg-medical-700 text-medical-500 dark:text-medical-400 hover:bg-medical-200 dark:hover:bg-medical-600'
            } disabled:opacity-50`}
            title={isListening ? 'Stop listening' : 'Voice input'}
          >
            {isListening ? (
              <>
                <MicOff className="h-4 w-4" />
                <span className="absolute inset-0 rounded-lg bg-red-600 animate-ping opacity-25" />
              </>
            ) : (
              <Mic className="h-4 w-4" />
            )}
          </button>
        )}

        {/* Send button */}
        <button
          onClick={handleSend}
          disabled={!input.trim() || isLoading}
          className="p-2 text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
          title="Send (Enter)"
        >
          {isLoading ? (
            <Loader className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Hint */}
      <div className="text-[10px] text-medical-400 dark:text-medical-500">
        {isListening ? (
          <span className="flex items-center gap-1 text-red-500 font-medium">
            <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
            Listening... auto-sends after 2s silence
          </span>
        ) : isLoading ? (
          <ThinkingIndicator size="sm" statusText="Working" />
        ) : (
          'Enter to send, Shift+Enter for new line'
        )}
      </div>
    </div>
  );
};
